import { useCallback, useEffect, useState } from 'react'
import { MatchHeader } from './components/MatchHeader'
import { AiReview } from './components/AiReview'
import { MatchStats } from './components/MatchStats'
import { Replay } from './components/Replay'
import { Home } from './components/Home'
import { Scouting, OpponentView } from './components/Scouting'
import { SeasonView } from './components/Season'
import { Help } from './components/Help'
import { sampleMatch, teamSummaries } from './data/match'
import { deepUrl, fetchScoreboard, replayUrl, signOut } from './data/api'
import type { MatchData } from './types'
import { Players } from './components/Players'
import { Rounds } from './components/Rounds'
import { Admin } from './components/Admin'
import { Debrief } from './components/Debrief'
import { Notes } from './components/Notes'
import { useIsAdmin, useSession } from './data/session'

type Route =
  | { v: 'home' }
  | { v: 'match'; id: string; round?: number }
  | { v: 'season' }
  | { v: 'players' }
  | { v: 'rounds' }
  | { v: 'scouting' }
  | { v: 'opponent'; id: string }
  | { v: 'admin' }
  | { v: 'help' }

/** Route <-> URL hash. Views were in-memory only, so there was no way to send anyone a link to a
 *  match — which matters the moment anything posts "report ready" into Discord, and matters just as
 *  much for a player wanting to share a round with the team. Hash rather than paths so it needs no
 *  server-side rewrite to work. */
function toHash(r: Route): string {
  switch (r.v) {
    case 'match':
      return r.round ? `#/match/${r.id}/${r.round}` : `#/match/${r.id}`
    case 'opponent':
      return `#/scouting/${r.id}`
    case 'home':
      return '#/'
    default:
      return `#/${r.v}`
  }
}

function fromHash(h: string): Route {
  const [, head, id, extra] = h.replace(/^#\/?/, '/').split('/')
  switch (head) {
    case 'match': {
      if (!id) return { v: 'home' }
      const round = Number(extra)
      return Number.isFinite(round) && round > 0 ? { v: 'match', id, round } : { v: 'match', id }
    }
    case 'scouting':
      return id ? { v: 'opponent', id } : { v: 'scouting' }
    case 'season':
      return { v: 'season' }
    case 'players':
      return { v: 'players' }
    case 'rounds':
      return { v: 'rounds' }
    case 'admin':
      return { v: 'admin' }
    case 'help':
      return { v: 'help' }
    default:
      return { v: 'home' }
  }
}

export function App() {
  const { user } = useSession()
  // Where a player's first question lives. A coach opens the portal asking "which game", a player
  // asking "how am I doing" — so with no route in the URL, they land on their own page instead of
  // the match list. Only with NO hash at all: a shared link to a match or round always wins, and
  // clicking the logo (which writes '#/') is an explicit trip to Matches.
  const landsOnOwnPage = !window.location.hash && user?.role === 'player'
  const [route, setRouteState] = useState<Route>(() =>
    landsOnOwnPage ? { v: 'players' } : fromHash(window.location.hash),
  )
  // Put that default in the URL without a history entry, so Back doesn't walk into an empty hash
  // and bounce them to Matches.
  useEffect(() => {
    if (landsOnOwnPage) window.history.replaceState(null, '', toHash({ v: 'players' }))
    // mount only — this is about the entry URL, not later navigation
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  // Writing the hash is what creates the history entry, so Back works without extra bookkeeping.
  const setRoute = useCallback((r: Route) => {
    const h = toHash(r)
    if (window.location.hash !== h) window.location.hash = h
    setRouteState(r)
  }, [])
  // The user editing the URL, hitting Back, or opening a shared link all arrive here.
  useEffect(() => {
    const onHash = () => setRouteState(fromHash(window.location.hash))
    window.addEventListener('hashchange', onHash)
    return () => window.removeEventListener('hashchange', onHash)
  }, [])
  // The replayer is the one view where screen real estate IS the feature — a 600px map in a 1024px
  // page is why ours read small next to cs2lens. Everything else stays narrow and readable.
  const [wide, setWide] = useState(false)
  // Four questions, four tabs: Matches = one game, Season = us over time, Players = one of us
  // over time, Scouting = them.
  const section =
    route.v === 'scouting' || route.v === 'opponent'
      ? 'scouting'
      : route.v === 'season'
        ? 'season'
        : route.v === 'players'
          ? 'players'
          : route.v === 'rounds'
            ? 'rounds'
            : route.v === 'help'
              ? 'help'
              : route.v === 'admin'
                ? 'admin'
                : 'matches'

  // The admin tab only exists for admins. Hiding it isn't the enforcement — /roster 403s for
  // everyone else — it just keeps a tab that leads nowhere out of five other people's way.
  const isAdmin = useIsAdmin()
  const tabs = (
    isAdmin
      ? (['matches', 'season', 'players', 'rounds', 'scouting', 'admin', 'help'] as const)
      : (['matches', 'season', 'players', 'rounds', 'scouting', 'help'] as const)
  ) as readonly string[]

  return (
    <div className={`mx-auto px-6 py-10 ${wide ? 'max-w-[1600px]' : 'max-w-5xl'}`}>
      <header className="mb-8 flex items-center justify-between">
        <button className="flex items-center gap-3" onClick={() => setRoute({ v: 'home' })}>
          <img
            src="/brand/jano-logo.png"
            alt="JANO"
            className="h-8 w-8"
            onError={(e) => (e.currentTarget.style.display = 'none')}
          />
          <div className="font-display text-lg font-bold leading-none">
            JANO <span className="text-accent">Demo Analyzer</span>
          </div>
        </button>
        {/* Scrollable rather than wrapping: four tabs plus a sign-out at 390px either crowd into
            each other or push the header to two rows. Sideways is the lesser evil for a nav, and
            the scrollbar is hidden so it doesn't read as a broken layout. */}
        <nav className="flex items-center gap-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {tabs.map((s) => (
            <button
              key={s}
              onClick={() =>
                setRoute(
                  s === 'matches'
                    ? { v: 'home' }
                    : s === 'season'
                      ? { v: 'season' }
                      : s === 'players'
                        ? { v: 'players' }
                        : s === 'rounds'
                          ? { v: 'rounds' }
                          : s === 'help'
                            ? { v: 'help' }
                            : s === 'admin'
                              ? { v: 'admin' }
                              : { v: 'scouting' },
                )
              }
              className="font-display px-3 py-1.5 text-xs uppercase tracking-wider"
              style={{
                borderRadius: 'var(--radius)',
                color: section === s ? 'var(--accent-ink)' : 'var(--text-dim)',
                background: section === s ? 'var(--accent)' : 'transparent',
                border: '1px solid var(--border)',
              }}
            >
              {s === 'players' && user?.role === 'player' ? 'my page' : s}
            </button>
          ))}
          <Whoami />
        </nav>
      </header>

      {route.v === 'home' ? (
        <Home onSelect={(id) => setRoute({ v: 'match', id })} onOpponent={(id) => setRoute({ v: 'opponent', id })} />
      ) : route.v === 'match' ? (
        <MatchView id={route.id} initialRound={route.round} onBack={() => setRoute({ v: 'home' })} onWide={setWide} />
      ) : route.v === 'help' ? (
        <Help />
      ) : route.v === 'players' ? (
        <Players onOpen={(id, round) => setRoute({ v: 'match', id, round })} />
      ) : route.v === 'rounds' ? (
        <Rounds onOpen={(id, round) => setRoute({ v: 'match', id, round })} />
      ) : route.v === 'admin' ? (
        <Admin />
      ) : route.v === 'season' ? (
        <SeasonView onSelect={(id) => setRoute({ v: 'match', id })} />
      ) : route.v === 'scouting' ? (
        <Scouting onOpen={(id) => setRoute({ v: 'opponent', id })} />
      ) : (
        <OpponentView id={route.id} onBack={() => setRoute({ v: 'scouting' })} />
      )}
    </div>
  )
}

/** Who you're signed in as, and the way out. */
function Whoami() {
  const { user } = useSession()
  if (!user) return null
  return (
    <span className="ml-2 flex items-center gap-2">
      <span
        title={`Signed in as ${user.name ?? user.steamid64}`}
        className="px-3 py-1.5 text-xs text-text-dim"
        style={{ borderRadius: 'var(--radius)', border: '1px solid var(--border)' }}
      >
        {user.name ?? user.steamid64.slice(-6)}
        <span className="ml-1.5 text-[10px] text-text-faint">{user.role}</span>
      </span>
      <button
        onClick={async () => {
          await signOut()
          window.location.reload()
        }}
        className="text-xs text-text-faint hover:text-loss"
      >
        sign out
      </button>
    </span>
  )
}

function MatchView({
  id,
  initialRound,
  onBack,
  onWide,
}: {
  id: string
  initialRound?: number
  onBack: () => void
  onWide?: (w: boolean) => void
}) {
  const [match, setMatch] = useState<MatchData | null>(id === 'sample' ? sampleMatch : null)
  const [err, setErr] = useState(false)
  const [tab, setTab] = useState<'stats' | 'replay' | 'ai' | 'notes'>(initialRound ? 'replay' : 'stats')
  // A note carries a round; clicking it should land in the replayer there, not just switch tabs.
  const [noteRound, setNoteRound] = useState<number | undefined>(undefined)
  useEffect(() => {
    onWide?.(tab === 'replay')
    return () => onWide?.(false)
  }, [tab, onWide])

  useEffect(() => {
    if (id === 'sample') {
      setMatch(sampleMatch)
      return
    }
    setMatch(null)
    setErr(false)
    fetchScoreboard(id)
      .then(setMatch)
      .catch(() => setErr(true))
  }, [id])

  return (
    <div className="space-y-5">
      <button onClick={onBack} className="text-xs text-text-dim hover:text-accent">
        ← matches
      </button>

      {err ? (
        <div className="border border-border bg-surface p-6 text-sm text-text-dim" style={{ borderRadius: 'var(--radius)' }}>
          Couldn't load this match — it may still be parsing. Try again shortly.
        </div>
      ) : !match ? (
        <div className="text-sm text-text-dim">Loading…</div>
      ) : (
        (() => {
          const teams = Object.keys(match.team_score)
          const ourTeam = teams.find((t) => /jano/i.test(t)) ?? teamSummaries(match)[0].name
          return (
            <>
              <MatchHeader match={match} ourTeam={ourTeam} />
              <nav className="flex gap-2">
                {(['stats', 'replay', 'ai', 'notes'] as const).map((t) => (
                  <button
                    key={t}
                    onClick={() => setTab(t)}
                    className="font-display px-4 py-1.5 text-sm uppercase tracking-wider"
                    style={{
                      borderRadius: 'var(--radius)',
                      color: tab === t ? 'var(--accent-ink)' : 'var(--text-dim)',
                      background: tab === t ? 'var(--accent)' : 'var(--surface)',
                      border: '1px solid var(--border)',
                    }}
                  >
                    {t === 'stats' ? 'Match stats' : t === 'replay' ? '2D replay' : t === 'ai' ? 'AI review' : 'Notes'}
                  </button>
                ))}
              </nav>
              {tab === 'stats' ? (
                <MatchStats match={match} ourTeam={ourTeam} deepUrl={deepUrl(id)} />
              ) : tab === 'replay' ? (
                <Replay url={replayUrl(id)} deepUrl={deepUrl(id)} initialRound={noteRound ?? initialRound} demoId={id === 'sample' ? undefined : id} />
              ) : tab === 'ai' ? (
                <AiReview id={id} />
              ) : (
                <>
                <Debrief
                  demoId={id}
                  rounds={match.rounds}
                  onRound={(r) => {
                    setNoteRound(r)
                    setTab('replay')
                  }}
                />
                <Notes
                  demoId={id}
                  rounds={match.rounds}
                  onRound={(r) => {
                    setNoteRound(r)
                    setTab('replay')
                  }}
                />
                </>
              )}
            </>
          )
        })()
      )}
    </div>
  )
}
